import { resolveLocationTimezone } from '../../domain/locationTimezone.js';
import { resolveOperatingStatus, type LocalDateTimeParts } from '../../domain/schedule.js';
import type { IsoWeekday, OperatingSchedule, WorldClock } from '../../domain/types.js';

export function worldTimezone(schedule: OperatingSchedule, locationRegion?: string): string {
  if (schedule.timezone?.trim()) {
    return schedule.timezone.trim();
  }
  if (schedule.timezoneStrategy === 'location') {
    const region = locationRegion?.trim();
    if (region) {
      const resolved = resolveLocationTimezone(region);
      if (resolved) return resolved;
    }
    return 'UTC';
  }
  if (schedule.timezoneStrategy === 'device') {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/** Prompt-facing world clock with year and IANA zone label. */
export function formatWorldClockPrompt(iso: string, schedule: OperatingSchedule, locationRegion?: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const tz = worldTimezone(schedule, locationRegion);
  const formatted = d.toLocaleString('en-US', {
    timeZone: tz,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  return `${formatted} (${tz})`;
}

const WEEKDAY_MAP: Record<string, IsoWeekday> = {
  Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7,
};

function partsInTimezone(iso: string, timeZone: string): { weekday: IsoWeekday; minutesSinceMidnight: number; date: string } {
  const d = new Date(iso);
  const weekdayShort = new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'short' }).format(d);
  const timeParts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(d);
  const hour = Number(timeParts.find((p) => p.type === 'hour')?.value ?? 0);
  const minute = Number(timeParts.find((p) => p.type === 'minute')?.value ?? 0);
  const date = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
  return {
    weekday: WEEKDAY_MAP[weekdayShort] ?? 1,
    minutesSinceMidnight: hour * 60 + minute,
    date,
  };
}

export function inWorldDateFromIso(iso: string, schedule: OperatingSchedule): string {
  return partsInTimezone(iso, worldTimezone(schedule)).date;
}

/** ISO week-start key (Monday) from a world-local YYYY-MM-DD calendar date. */
export function inWorldWeekKeyFromDate(inWorldDate: string): string {
  const [y, m, d] = inWorldDate.split('-').map(Number);
  const anchor = new Date(Date.UTC(y, m - 1, d, 12));
  const dow = anchor.getUTCDay();
  const diff = dow === 0 ? -6 : 1 - dow;
  anchor.setUTCDate(anchor.getUTCDate() + diff);
  return anchor.toISOString().slice(0, 10);
}

export function localPartsFromWorldIso(iso: string, schedule: OperatingSchedule): LocalDateTimeParts {
  const { weekday, minutesSinceMidnight } = partsInTimezone(iso, worldTimezone(schedule));
  return { weekday, minutesSinceMidnight };
}

export function advanceWorldClock(clock: WorldClock, deltaMs: number): WorldClock {
  const base = new Date(clock.inWorldIso).getTime();
  const next = new Date(base + deltaMs).toISOString();
  return { ...clock, inWorldIso: next };
}

export function syncClockToNow(
  clock: WorldClock,
  anchorMs: number,
  nowMs = Date.now(),
): { clock: WorldClock; anchorMs: number } {
  const delta = Math.max(0, nowMs - anchorMs);
  if (delta === 0) return { clock, anchorMs };
  return { clock: advanceWorldClock(clock, delta), anchorMs: nowMs };
}

export function operatingStatusForClock(
  schedule: OperatingSchedule,
  inWorldIso: string,
): ReturnType<typeof resolveOperatingStatus> {
  return resolveOperatingStatus(schedule, localPartsFromWorldIso(inWorldIso, schedule));
}

export function formatWorldClock(clock: WorldClock, schedule: OperatingSchedule): string {
  const d = new Date(clock.inWorldIso);
  if (Number.isNaN(d.getTime())) return clock.inWorldIso;
  return d.toLocaleString(undefined, {
    timeZone: worldTimezone(schedule),
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}
